import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, AlertTriangle, CheckSquare, Square, Trash, Image as ImageIcon, Package } from 'lucide-react';
import { useProdutos, useDeletarProduto, useBulkDeletarProdutos } from '@/hooks/useProdutos';
import { useCategorias } from '@/hooks/useCategorias';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export function ListaProdutos() {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busca, setBusca] = useState('');

  const { data: produtos, isLoading } = useProdutos(busca ? { busca } : undefined);
  const { data: categorias } = useCategorias();
  
  const deletarMutation = useDeletarProduto();
  const bulkDeletarMutation = useBulkDeletarProdutos();

  const toggleSelectAll = () => {
    if (selectedIds.length === (produtos?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(produtos?.map((p: any) => p.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Deseja excluir os ${selectedIds.length} produtos selecionados?`)) {
      bulkDeletarMutation.mutate(selectedIds, {
        onSuccess: (response: any) => {
          setSelectedIds([]);
          alert(response?.data?.message || 'Exclusão em massa concluída!');
        },
        onError: () => {
          alert('Erro ao realizar exclusão em massa');
        }
      });
    }
  };

  if (isLoading) return <div className="p-8 text-center text-gray-500 text-lg font-medium animate-pulse">Carregando produtos...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Produtos</h2>
          <p className="text-gray-500">Gerencie seu catálogo</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50 transition-colors"
              onClick={handleBulkDelete}
              disabled={bulkDeletarMutation.isPending}
            >
              <Trash className="h-4 w-4 mr-2" />
              Excluir ({selectedIds.length})
            </Button>
          )}
          <Button 
            className="bg-[#ED7347] hover:bg-[#D5653C] text-white border-none"
            onClick={() => navigate('/admin/produtos/novo')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Produto
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6">
        <div className="mb-6">
          <Input 
            placeholder="Buscar produtos..." 
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="max-w-sm rounded-[10px]"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead className="border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 w-10">
                  <button onClick={toggleSelectAll} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                    {selectedIds.length === (produtos?.length || 0) && selectedIds.length > 0 ? (
                      <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-sm font-medium text-gray-500">Produto</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-500">SKU</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-500">Preço</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-500">Estoque</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-500 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {produtos?.map((prod: any) => {
                const categoria = categorias?.find((c: any) => c.id === prod.categoriaId);
                const isEstoqueBaixo = Number(prod.estoqueDisponivel) <= Number(prod.estoqueMinimo || 0);

                return (
                  <tr key={prod.id} className={`hover:bg-gray-50/50 transition-colors ${selectedIds.includes(prod.id) ? 'bg-orange-50/30' : ''}`}>
                    <td className="px-4 py-4">
                      <button onClick={() => toggleSelect(prod.id)} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                        {selectedIds.includes(prod.id) ? (
                          <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center shrink-0">
                          {prod.fotos && prod.fotos.length > 0 ? (
                            <img src={prod.fotos[0]} alt={prod.nome} className="w-10 h-10 object-contain rounded-md" />
                          ) : (
                            <div className="w-10 h-10 bg-gray-100 rounded-md flex flex-col items-center justify-center text-[10px] text-gray-400 leading-tight">
                              <ImageIcon className="h-4 w-4 mb-0.5" />
                              <span className="truncate w-8 text-center" title="Prodi">Prodi</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900 text-sm">{prod.nome}</span>
                          <span className="text-xs text-gray-500">{categoria ? categoria.nome : 'Sem categoria'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><span className="text-sm text-gray-500 font-mono tracking-tighter">{prod.skuInterno || '-'}</span></td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900">R$ {Number(prod.precoVenda || 0).toFixed(2).replace('.', ',')}</span>
                        <span className="text-[11px] text-gray-400 font-medium">Custo: R$ {Number(prod.custo || prod.custoAtual || 13.13).toFixed(2).replace('.', ',')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5 font-bold">
                        <span className={isEstoqueBaixo ? 'text-red-500' : 'text-gray-900'}>{prod.estoqueDisponivel}</span>
                        {isEstoqueBaixo && <AlertTriangle className="h-3.5 w-3.5 text-red-500 stroke-[2.5]" />}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {prod.ativo ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-50 text-green-600 border border-green-100">
                          Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200">
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex justify-end gap-3 items-center">
                        <button
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          className="text-blue-500 hover:text-blue-600 transition-colors"
                          onClick={() => navigate(`/admin/produtos/${prod.id}/editar`)}
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          className="text-red-500 hover:text-red-600 transition-colors"
                          onClick={() => {
                            if (window.confirm(`Deseja excluir o produto "${prod.nome}"?`)) {
                              deletarMutation.mutate(prod.id, {
                                onError: (error: any) => alert(error.response?.data?.error?.message || 'Erro ao excluir produto')
                              });
                            }
                          }}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(!produtos || produtos.length === 0) && (
            <div className="p-12 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">Nenhum produto encontrado</p>
              <p className="text-sm mt-1">Ajuste os filtros ou cadastre um novo produto.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}